import {resolvePointColor} from '../PanelMediaBrowser/common';
import {POINT_CLOUD_DRAW} from './pointCloudDrawConfig';
import {
  type PointCloudCardRenderRequest,
  type PointCloudFullscreenRenderRequest,
  type PointCloudRenderer,
  type PointCloudRgb,
  type PointCloudScene,
} from './types';

type PotreeDenseDraw = typeof import('./potreeDenseDraw');

let potreeDenseDrawPromise: Promise<PotreeDenseDraw> | undefined;

function loadPotreeDenseDraw(): Promise<PotreeDenseDraw> {
  potreeDenseDrawPromise ??= import('./potreeDenseDraw');
  return potreeDenseDrawPromise;
}

/**
 * Resolves category/default colors for the dense Potree draw path. Truncation
 * stays in preprocess; this only packs colors the same way
 * {@link sceneToBabylonCloud} does.
 */
export function sceneToColoredPotreeCloud(
  scene: PointCloudScene,
  pointColor?: PointCloudRgb
) {
  return {
    points: scene.points.map(point => ({
      position: point.position,
      color: pointColor ?? resolvePointColor(point),
    })),
    vectors: scene.vectors.map(vector => ({
      start: vector.start,
      end: vector.end,
      color: vector.color ?? POINT_CLOUD_DRAW.defaultVectorColor,
    })),
    boxes: scene.boxes,
  };
}

/**
 * Potree adapter for {@link PointCloudRenderer}. Lazy-loads
 * {@link ./potreeDenseDraw}. Selected by PointCloud when the entity gate
 * `media_pointcloud_potree_renderer` is on.
 */
export const potreePointCloudRenderer: PointCloudRenderer = {
  id: 'potree',

  async renderCard(request: PointCloudCardRenderRequest) {
    const denseDraw = await loadPotreeDenseDraw();
    const colored = sceneToColoredPotreeCloud(
      request.scene,
      request.pointColor
    );
    return denseDraw.renderCardScreenshot({
      scene: colored,
      width: request.width,
      height: request.height,
      backgroundColor: request.backgroundColor,
      useRightHandedSystem: request.useRightHandedSystem,
      cameraControl: request.cameraControl,
    });
  },

  async mountFullscreen(request: PointCloudFullscreenRenderRequest) {
    const denseDraw = await loadPotreeDenseDraw();
    const colored = sceneToColoredPotreeCloud(
      request.scene,
      request.pointColor
    );
    // Matches Babylon adapter: fullscreen does not pass cameraControl.
    return denseDraw.mountFullscreenSession({
      scene: colored,
      backgroundColor: request.backgroundColor,
      useRightHandedSystem: request.useRightHandedSystem,
    });
  },
};
